<template>
  <main
    class="campaign-area"
    :dir="uiLocale === 'en' ? 'ltr' : 'rtl'"
    :style="{ textAlign: uiLocale === 'en' ? 'left' : 'right' }"
  >
    <header class="campaign-header">
      <div><h1>{{ words.title }}</h1><p>{{ words.intro }}</p></div><router-link
        v-if="$can('manage', 'contentCampaign')"
        class="campaign-button primary"
        :to="{ name: 'content-campaign-create' }"
      >
        {{ words.create }}
      </router-link>
    </header>
    <form
      class="campaign-tools"
      @submit.prevent="filter"
    >
      <input
        v-model="search"
        :aria-label="words.search"
        :placeholder="words.search"
        maxlength="100"
      ><select
        v-model="status"
        :aria-label="words.statusFilter"
      >
        <option value="">
          {{ words.all }}
        </option><option
          v-for="state in states"
          :key="state"
          :value="state"
        >
          {{ words[state] }}
        </option>
      </select><button :disabled="loading">
        {{ words.filter }}
      </button><router-link
        v-if="$can('report', 'contentCampaign')"
        class="campaign-button"
        :to="{ name: 'content-campaign-reports' }"
      >
        {{ words.reports }}
      </router-link>
    </form>
    <p
      v-if="message"
      class="campaign-message"
      role="alert"
    >
      {{ message }}
    </p><p
      v-if="loading"
      role="status"
    >
      {{ words.loading }}
    </p>
    <section
      class="campaign-panel campaign-table-wrap"
      :aria-busy="loading"
      :aria-label="words.listRegion"
      tabindex="0"
    >
      <button
        v-if="loadFailed && !loading"
        type="button"
        @click="load"
      >
        {{ words.retryList }}
      </button>
      <template v-else-if="loaded && !loading && !items.length">
        <p>{{ filtered ? words.noMatches : words.empty }}</p>
        <button
          v-if="filtered"
          type="button"
          @click="clearFilters"
        >
          {{ words.clearFilters }}
        </button>
      </template><table v-else-if="loaded && !loading && !loadFailed && items.length">
        <thead><tr><th>{{ words.name }}</th><th>{{ words.status }}</th><th>{{ words.language }}</th><th>{{ words.priority }}</th><th>{{ words.updated }}</th><th>{{ words.edit }}</th></tr></thead><tbody><tr
          v-for="item in items"
          :key="item.id"
        >
          <td>{{ item.internalName }}<small>{{ item.startsAt ? date(item.startsAt, item.displayTimezone) : '—' }} · {{ item.endsAt ? date(item.endsAt, item.displayTimezone) : '—' }} · {{ item.displayTimezone }}</small></td><td><span class="campaign-state">{{ words[item.effectiveState || item.status] || item.status }}</span></td><td>{{ languages(item) }}</td><td>{{ item.priority }}</td><td>{{ date(item.updatedAt) }}</td><td class="campaign-row-actions"><router-link
            class="campaign-button"
            :to="{ name: 'content-campaign-edit', params: { id: item.id } }"
          >
            {{ words.edit }}
          </router-link><router-link
            v-if="$can('report', 'contentCampaign')"
            class="campaign-button"
            :to="{ name: 'content-campaign-reports', query: { campaignId: item.id } }"
          >
            {{ words.viewStats }}
          </router-link></td>
        </tr></tbody>
      </table>
    </section>
    <nav
      v-if="loaded && !loading && !loadFailed && total > limit"
      class="campaign-actions"
      :aria-label="words.page"
    >
      <button
        :disabled="loading || page <= 1"
        @click="go(page - 1)"
      >
        {{ words.previous }}
      </button><span>{{ words.page }} {{ page }} / {{ pages }}</span><button
        :disabled="loading || page >= pages"
        @click="go(page + 1)"
      >
        {{ words.next }}
      </button>
    </nav>
  </main>
</template>
<script>
import axios from '@/libs/axios'
import messages from './messages'
import { errorKey } from './model'
import './campaign.css'

export default {
  data() {
    return {
      items: [], total: 0, page: 1, limit: 20, search: '', status: '', loading: false, loaded: false, loadFailed: false, message: '', sequence: 0, states: ['draft', 'published', 'paused', 'archived'],
    }
  },
  computed: {
    filtered() { return !!this.search.trim() || !!this.status }, uiLocale() { return this.$i18n.locale }, words() { return messages[this.uiLocale] || messages.fa }, pages() { return Math.max(1, Math.ceil(this.total / this.limit)) },
  },
  created() { this.load() },
  beforeDestroy() { this.sequence += 1 },
  methods: {
    date(value, timezone = 'Asia/Tehran') { if (!value) return '—'; try { return new Date(value).toLocaleString(this.uiLocale, { timeZone: timezone }) } catch (error) { return this.words.unknown } },
    languages(item) { return ['fa', 'en', 'ar'].filter(code => item.locales && item.locales[code] && item.locales[code].enabled).map(code => ({ fa: 'فارسی', en: 'English', ar: 'العربية' })[code]).join(' · ') || '—' },
    clearFilters() { this.search = ''; this.status = ''; this.filter() },
    filter() { this.page = 1; this.load() },
    go(page) { this.page = page; this.load() },
    async load() {
      this.sequence += 1; const { sequence } = this; this.loading = true; this.loadFailed = false; this.message = ''; this.items = []; this.total = 0
      try {
        const params = { page: this.page, limit: this.limit }; if (this.search.trim()) params.search = this.search.trim(); if (this.status) params.status = this.status
        const response = await axios.get('content-campaigns', { params, timeout: 10000 })
        if (sequence !== this.sequence) return
        if (!response.data || !Array.isArray(response.data.items) || !Number.isInteger(response.data.total) || response.data.total < 0) throw new Error('INVALID_LIST')
        this.items = response.data.items; this.total = response.data.total; this.loaded = true
      } catch (error) { if (sequence === this.sequence) { const key = errorKey(error); this.message = key === 'failed' ? this.words.listFailed : this.words[key]; this.loadFailed = true; this.loaded = false } } finally { if (sequence === this.sequence) this.loading = false }
    },
  },
}
</script>
